// src/oura/newscan/recipeShape.ts
//
// The recipe's shape in one line, the same words on every step:
//   "26 about the outlets (14 Bangsar, 12 Subang Jaya) · 14 about what they offer · 10 about price and booking"
//   "14 about the outlet · 20 about what they offer · 16 about price and booking"      one outlet
//   "14 about the showroom · 20 about what they offer · 16 about price and booking"    both (Local v4.5)
//   "26 about the location, 1 place under 3 area names (10 Bukit Tinggi, 8 Janda Baik, 8 Bentong) · 14 about …"
//                                                                    one place with other area names
//   "28 about being recommended · 14 about your site as the source · 8 about business buyers"   brand (Online, v4.5)
//   "36 about being recommended · 14 about your site as the source"                             brand, shoppers only
//
// Before a draft exists the shape is worked out from the business (how
// customers buy and how many outlets count). Once there is a draft it is read
// from the 50 themselves, so it always matches what Review lists.

import type { ScanDraft, ScanDraftBusiness, ScanDraftOutlet } from "@service/aeo/scanDraftTypes";
import { OURA_AREA_NAMES_ENABLED } from "@oura/newscan/flags";
import { areaNames, otherAreaNames } from "@oura/newscan/singleVenue";
import { usableTown } from "@oura/newscan/towns";

/** Location questions with two or more outlets; one outlet (or Online) asks 14. */
export const LOCATION_TOTAL = 26;
export const SINGLE_LOCATION_TOTAL = 14;

/** Online stores sell, they do not book: "price and buying". */
const tail = (specialty: number, intent: number, buy?: string) =>
    `${specialty} about what they offer · ${intent} about price and ${buy === "online" ? "buying" : "booking"}`;
/** "26 about the outlets (14 Bangsar, 12 Subang Jaya)", or "14 about the outlet" for one place; Both says showroom. */
const outletsPart = (total: number, split: Array<[string, number]>, noun: "outlet" | "showroom" = "outlet") =>
    split.length > 1 ? `${total} about the ${noun}s (${split.map(([name, n]) => `${n} ${name}`).join(", ")})` : `${total} about the ${noun}`;
const nounOf = (buy?: string): "outlet" | "showroom" => (buy === "both" ? "showroom" : "outlet");
/** One place asked about under several area names: one location, never read as several outlets. */
const locationPart = (total: number, split: Array<[string, number]>) =>
    `${total} about the location, 1 place under ${split.length} area names (${split.map(([name, n]) => `${n} ${name}`).join(", ")})`;
/** The one counted place's names when it has others ("Bukit Tinggi", "Janda Baik", "Bentong"), else null. Visit us only. */
function namesOfOnePlace(b: ScanDraftBusiness): string[] | null {
    const places = countedOutlets(b.outlets);
    if (!OURA_AREA_NAMES_ENABLED || b.buy !== "visit" || places.length !== 1 || otherAreaNames(places[0]).length === 0) return null;
    return areaNames(places[0]);
}

/** Split as evenly as possible, flagship strictly larger: 14/12, 10/8/8, 8/6/6/6, 6/5/5/5/5. */
export function outletShares(outlets: number, total: number): number[] {
    if (outlets <= 0) return [];
    if (outlets === 1) return [total];
    if (outlets >= total) return Array.from({ length: outlets }, (_, i) => (i < total ? 1 : 0));
    const base = Math.floor(total / outlets);
    const shares = Array.from({ length: outlets }, () => base);
    const rest = total - base * outlets;
    shares[0] += rest;
    if (rest === 0) {
        shares[0] += 1;
        shares[outlets - 1] -= 1;
    }
    return shares;
}

/**
 * Area-wide questions: every question that is not about one outlet ("best
 * dental clinic in ___?"). The blank is a town. With two or more outlets
 * these are the 14 + 10; with one, the 20 + 16.
 */
export const AREA_TOTAL = 24;
export const SINGLE_AREA_TOTAL = 36;

/**
 * How many of `n` area-wide questions each town gets: by how many outlets sit
 * in it, at least `minimum` each (aeo_query_spec._town_quota, mirrored).
 */
export function townQuota(towns: string[], n: number, minimum = 2): Array<[string, number]> {
    const distinct = towns.filter((t, i) => t && towns.indexOf(t) === i);
    if (!distinct.length || n <= 0) return [];
    const floor = distinct.length * minimum > n ? Math.max(1, Math.floor(n / distinct.length)) : minimum;
    const weight = (t: string) => towns.filter((x) => x === t).length;
    const total = towns.filter(Boolean).length || 1;
    const exact = new Map(distinct.map((t) => [t, (n * weight(t)) / total]));
    const quota = new Map(distinct.map((t) => [t, Math.max(floor, Math.floor(exact.get(t) ?? 0))]));
    const sum = () => Array.from(quota.values()).reduce((a, b) => a + b, 0);
    while (sum() > n) {
        const above = distinct.filter((t) => (quota.get(t) ?? 0) > floor);
        const from = (above.length ? above : distinct).reduce((a, b) => ((quota.get(b) ?? 0) > (quota.get(a) ?? 0) ? b : a));
        quota.set(from, (quota.get(from) ?? 0) - 1);
    }
    while (sum() < n) {
        const to = distinct.reduce((a, b) => ((exact.get(b) ?? 0) - (quota.get(b) ?? 0) > (exact.get(a) ?? 0) - (quota.get(a) ?? 0) ? b : a));
        quota.set(to, (quota.get(to) ?? 0) + 1);
    }
    return distinct.map((t): [string, number] => [t, quota.get(t) ?? 0]).sort((a, b) => b[1] - a[1]);
}

/**
 * Which towns the area-wide questions will name, and how many each: "24, Puchong
 * 6, Shah Alam 6, …". Only the places that get questions count; a blank town is
 * the business's city. A place with no usable town (blank with no city, or a
 * state such as Selangor) is listed in `missing` and the split stays empty, so
 * the line never shows a state or a made-up town. Null with no counted place.
 */
export function townSplit(
    outlets: ScanDraftOutlet[],
    city: string,
    oneVenueNames = false,
): { total: number; split: Array<[string, number]>; missing: string[] } | null {
    const places = countedOutlets(outlets);
    if (!places.length) return null;
    const towns = places.map((o) => usableTown(o.town) || usableTown(city));
    const missing = places.filter((_, i) => !towns[i]).map((o) => o.name.trim());
    // One place under several area names asks the 24 of a multi-outlet set (see shapeFromBusiness).
    const total = places.length > 1 || oneVenueNames ? AREA_TOTAL : SINGLE_AREA_TOTAL;
    if (missing.length) return { total, split: [], missing };
    if (places.length === 1) return { total, split: [[towns[0], total]], missing };
    return { total, split: townQuota(towns, AREA_TOTAL), missing };
}

/** The places that get questions: the ones the site names, and staff ones staff confirmed. */
function countedOutlets(outlets: ScanDraftOutlet[]): ScanDraftOutlet[] {
    return outlets.filter((o) => o.name.trim() && (o.source === "site" || o.confirmed === true));
}

/* ------------------------------------------------------------------------- *
 * Brand mode (v4.5, Online): found by what it sells, not by place
 * ------------------------------------------------------------------------- */

/** modes.brand.checks: 28 recommend you, 14 cite your site, 8 business buyers (0 for shoppers only, the 8 join the 28). */
export const BRAND_PICK = 28;
export const BRAND_SOURCE = 14;
export const BRAND_SUPPLIER = 8;

/** A brand draft: drafted Online, or carrying brand counts. */
export function isBrandDraft(d: ScanDraft): boolean {
    return d.recipe.shape === "online" || d.business.mode === "brand" || typeof d.counts.check.pick === "number";
}

/** The brand counts of a drafted 50. */
export function brandCounts(d: ScanDraft): { pick: number; source: number; supplier: number } {
    const c = d.counts.check;
    return { pick: c.pick ?? 0, source: c.source ?? 0, supplier: c.supplier ?? 0 };
}

/** The recipe line: "28 about being recommended · 14 about your site as the source · 8 about business buyers". */
function brandLong(pick: number, source: number, supplier: number): string {
    const parts = [`${pick} about being recommended`, `${source} about your site as the source`];
    if (supplier) parts.push(`${supplier} about business buyers`);
    return parts.join(" · ");
}

/** The brand shape before a draft exists: by whether it also sells to businesses. */
export function brandShapeFromBusiness(b: ScanDraftBusiness): string {
    const supplier = b.sellsToBusinesses === true ? BRAND_SUPPLIER : 0;
    return brandLong(BRAND_PICK + (BRAND_SUPPLIER - supplier), BRAND_SOURCE, supplier);
}

/** The shape the business would be drafted with, before a draft exists. */
export function shapeFromBusiness(b: ScanDraftBusiness): string {
    if (b.buy === "online") return brandShapeFromBusiness(b);
    // Visit us, Both (its showrooms are the outlets) and We go to them: Local v4.5 (the v4.2 problem-led questions, unchanged).
    const noun = nounOf(b.buy);
    const places = countedOutlets(b.outlets);
    const names = namesOfOnePlace(b);
    if (names) {
        const shares = outletShares(names.length, LOCATION_TOTAL);
        return `${locationPart(LOCATION_TOTAL, names.map((n, i): [string, number] => [n, shares[i]]))} · ${tail(14, 10)}`;
    }
    if (places.length <= 1) return `${outletsPart(SINGLE_LOCATION_TOTAL, [], noun)} · ${tail(20, 16)}`;
    const shares = outletShares(places.length, LOCATION_TOTAL);
    return `${outletsPart(LOCATION_TOTAL, places.map((o, i): [string, number] => [o.name.trim(), shares[i]]), noun)} · ${tail(14, 10)}`;
}

/** The shape of a drafted 50, read from its counts and its location questions. */
export function shapeFromDraft(d: ScanDraft): string {
    if (isBrandDraft(d)) {
        const { pick, source, supplier } = brandCounts(d);
        return brandLong(pick, source, supplier);
    }
    const { location, specialty, intent } = d.counts.check;
    // The buy mode the 50 were drafted with; the business may have moved on since.
    const buy = d.recipe.shape;
    const end = tail(specialty, intent, buy);

    // Keyed in lower case, shown as the first question wrote the place.
    const perPlace = new Map<string, { name: string; n: number }>();
    d.questions.forEach((q) => {
        if (q.check !== "location") return;
        const name = q.fill.trim();
        const key = name.toLowerCase();
        const was = perPlace.get(key);
        perPlace.set(key, { name: was?.name ?? name, n: (was?.n ?? 0) + 1 });
    });

    const split = Array.from(perPlace.values()).map((p): [string, number] => [p.name, p.n]);
    if (split.length > 1 && namesOfOnePlace(d.business)) return `${locationPart(location, split)} · ${end}`;
    return `${outletsPart(location, split, nounOf(buy))} · ${end}`;
}
